import { Link, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import PageHero from "../components/PageHero";
import heroImage from "../assets/zeamays.png";
import { loadUserOrders } from "../services/orders";
import { formatPrice } from "../data/products";

const statusLabel = (status) => String(status || "pending").replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());

const Profile = () => {
  const { user, loading } = useAuth();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (!user?.id) return;
    loadUserOrders(user.id).then(setOrders).catch(() => setOrders([]));
  }, [user?.id]);

  if (loading) {
    return (
      <div className="min-h-screen px-6 py-24">
        <div className="mx-auto max-w-3xl rounded-[2rem] bg-white p-10 text-center shadow-lg shadow-black/5">
          <p className="text-lg text-slate-700">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const avatarUrl = user.user_metadata?.avatar_url;
  const fullName =
    user.user_metadata?.full_name || user.user_metadata?.name || "Zea Mays Customer";

  return (
    <div className="min-h-screen bg-[#f7fbef]">
      <PageHero
        eyebrow="Your account"
        title="Keep your profile and orders in one polished place."
        description="Review your details, reopen your cart, and jump back into your order history without losing context."
        image={heroImage}
        imageAlt="Zea Mays customer profile"
        primaryAction={{ to: "/orders", label: "View Orders" }}
        secondaryAction={{ to: "/products", label: "Shop Products" }}
        stats={[
          { label: "Signed in", value: user.email || "Customer" },
          { label: "Orders", value: `${orders.length}` },
          { label: "Access", value: "Secure" },
          { label: "Cart", value: "Ready" },
        ]}
        compact
      />

      <div className="px-6 py-12">
        <div className="mx-auto max-w-4xl rounded-[2rem] bg-white p-8 shadow-lg shadow-black/5">
          <div className="flex flex-col items-center gap-5 text-center sm:flex-row sm:text-left">
            {avatarUrl ? (
              <img src={avatarUrl} alt={fullName} className="h-24 w-24 rounded-full object-cover" />
            ) : (
              <div className="flex h-24 w-24 items-center justify-center rounded-full bg-red-700 text-3xl font-bold text-white">
                {fullName.charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <p className="text-sm uppercase tracking-[0.28em] text-red-700">Profile</p>
              <h1 className="mt-3 text-4xl font-bold text-slate-900">{fullName}</h1>
              <p className="mt-2 text-lg text-slate-600">{user.email}</p>
            </div>
          </div>

          <div className="mt-8 grid gap-6 md:grid-cols-2">
            <div className="rounded-[2rem] bg-slate-50 p-6">
              <p className="text-sm uppercase tracking-[0.2em] text-slate-500">Account</p>
              <p className="mt-4 text-slate-900">
                Signed in with {user.app_metadata?.provider || "email"}.
              </p>
              <p className="mt-2 text-slate-600">
                Your cart and session stay synced automatically whenever you return.
              </p>
            </div>
            <div className="rounded-[2rem] bg-slate-900 p-6 text-white">
              <p className="text-sm uppercase tracking-[0.2em] text-slate-400">Quick links</p>
              <div className="mt-4 flex flex-wrap gap-3">
                <Link
                  to="/order"
                  className="rounded-full bg-white px-5 py-3 font-semibold text-slate-900 transition hover:bg-slate-100"
                >
                  View orders
                </Link>
                <Link
                  to="/cart"
                  className="rounded-full border border-white/20 px-5 py-3 font-semibold text-white transition hover:border-white/40"
                >
                  Open cart
                </Link>
              </div>
            </div>
          </div>

          <section className="mt-8 rounded-[2rem] bg-slate-50 p-6">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm uppercase tracking-[0.2em] text-slate-500">My Orders</p>
                <h2 className="mt-2 text-2xl font-bold text-slate-900">Recent orders</h2>
              </div>
              <Link to="/orders" className="text-sm font-semibold text-red-700 hover:text-red-800">
                View all
              </Link>
            </div>
            {orders.length ? <div className="mt-5 space-y-3">
              {orders.slice(0, 5).map((order) => <Link key={order.id} to="/orders" className="flex flex-col gap-2 rounded-2xl bg-white px-4 py-4 shadow-sm transition hover:shadow md:grid md:grid-cols-4 md:items-center">
                <span className="font-semibold text-slate-950">{order.payment_reference || order.id}</span>
                <span className="text-sm text-slate-600">{new Intl.DateTimeFormat("en-NG", { dateStyle: "medium" }).format(new Date(order.created_at))}</span>
                <span className="font-semibold text-slate-800">{formatPrice(order.total_amount)}</span>
                <span className="w-fit rounded-full bg-amber-50 px-3 py-1 text-sm font-semibold text-amber-800">{statusLabel(order.status)}</span>
              </Link>)}
            </div> : <p className="mt-5 text-slate-600">You have no orders yet.</p>}
          </section>
        </div>
      </div>
    </div>
  );
};

export default Profile;
